import { Stack } from '@/components/layout';
import { Avatar } from '@/components/ui';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { memo, type ComponentProps } from 'react';
import { StyleSheet } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

export type MyKidCardAvatarSource = ComponentProps<typeof Avatar.Image>['source'];

export type MyKidCardAvatarProps = {
  color: string;
  progress: number;
  source: MyKidCardAvatarSource;
  hasAvatar: boolean;
};

function MyKidCardAvatar({ color, progress, source, hasAvatar }: MyKidCardAvatarProps) {
  const { scale } = useScaling();

  const ringSize = scale(60);
  const ringStrokeWidth = scale(4);
  const avatarSize = scale(48);

  const radius = ringSize / 2 - ringStrokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <Stack width={ringSize} height={ringSize} align="center" justify="center">
      <Svg width={ringSize} height={ringSize} style={StyleSheet.absoluteFill}>
        <Circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={radius}
          stroke={color['0']}
          strokeWidth={ringStrokeWidth}
          fill="none"
        />
        <Circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={radius}
          stroke={color}
          strokeWidth={ringStrokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
        />
      </Svg>

      <Avatar size={avatarSize} borderWidth={0} activeOpacity={1}>
        <Avatar.Image
          source={source}
          size={hasAvatar ? undefined : avatarSize}
          disableCircular={!hasAvatar}
          backgroundColor={colors['1']}
        />
      </Avatar>
    </Stack>
  );
}

export default memo(MyKidCardAvatar);
